import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  BsArrowRight, 
  BsArrowUpRight, 
  BsPlayFill, 
  BsGlobe, 
  BsRobot, 
  BsMegaphone, 
  BsLayers, 
  BsPalette, 
  BsFolderFill,
  BsChevronLeft,
  BsChevronRight,
  BsSearch,
  BsCalendarCheck,
  BsCodeSlash,
  BsRocketTakeoff,
  BsGraphUpArrow,
  BsX
} from "react-icons/bs";
import { useCMS } from "../../context/CMSContext";

const getProjectImageUrl = (item) => {
  const img = item?.image || item?.img;
  if (!img) return "/assets/projects/mistore_ecommerce.jpg";
  if (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:")) {
    return img;
  }
  if (img.startsWith("/assets/") || img.startsWith("assets/")) {
    return img.startsWith("/") ? img : `/${img}`;
  }
  const baseUrl = window.API_URL || "http://localhost:8000";
  const cleanImg = img.replace(/^\/+/, "");
  if (cleanImg.startsWith("uploads/")) {
    return `${baseUrl}/${cleanImg}`;
  }
  return `${baseUrl}/uploads/${cleanImg}`;
};

const getCategory = (item) => {
  if (item.category) return item.category;
  const text = `${item.title || ""} ${item.desc || ""}`.toLowerCase();
  if (text.includes("ai") || text.includes("bot") || text.includes("intelligence") || text.includes("automation")) {
    return "AI Solution";
  }
  if (text.includes("shop") || text.includes("store") || text.includes("commerce") || text.includes("cart") || text.includes("furniture") || text.includes("perfume")) {
    return "E-Commerce";
  }
  if (text.includes("market") || text.includes("seo") || text.includes("ads") || text.includes("growth") || text.includes("campaign") || text.includes("tourism") || text.includes("tour")) {
    return "Digital Marketing";
  }
  if (text.includes("app") || text.includes("saas") || text.includes("portal") || text.includes("erp") || text.includes("crm") || text.includes("fintech") || text.includes("dashboard") || text.includes("management") || text.includes("system")) {
    return "Web Application";
  }
  return "Website";
};

const filterCategories = ["All", "Websites", "AI Solutions", "Digital Marketing", "Web-Apps"];

const defaultProcessSteps = [
  {
    num: "01",
    title: "Discover",
    desc: "We understand your goals, audience, and challenges."
  },
  {
    num: "02",
    title: "Plan",
    desc: "We create a strategy and roadmap tailored to your needs."
  },
  {
    num: "03",
    title: "Design",
    desc: "Our team designs modern, user-focused experiences."
  },
  {
    num: "04",
    title: "Develop",
    desc: "We build with the latest technologies and best practices."
  },
  {
    num: "05",
    title: "Launch",
    desc: "We test, deploy and ensure a smooth launch."
  },
  {
    num: "06",
    title: "Grow",
    desc: "We provide ongoing support and strategies for continuous growth."
  }
];

const testimonials = [
  {
    quote: "Beta Communes built an amazing website for our brand. Professional team, great communication, and outstanding results!",
    name: "Sarah Khan",
    role: "Founder, MiStore",
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&auto=format&fit=crop&q=80"
  },
  {
    quote: "Their AI solutions helped us automate our workflow and save hundreds of hours. Highly recommended!",
    name: "Ali Raza",
    role: "CEO, Aman Labs",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80"
  },
  {
    quote: "The digital marketing campaign brought us 3x more bookings. Truly a results-driven team!",
    name: "James Miller",
    role: "Director, Thrill Buses",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80"
  }
];

const Portfolio = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [activeTestimonialIdx, setActiveTestimonialIdx] = useState(0);
  const { getSection } = useCMS();

  const portfolioCMS = getSection('portfolio_page', {
    heroEyebrow: "OUR WORK",
    heroTitleLine1: "Ideas Turned Into",
    heroHighlight: "Real Results",
    heroSubtitle: "Explore how we've helped businesses build, scale and transform through design, development, AI and digital marketing",
    processHeading: "How We Turn Ideas Into Successful Projects",
    processSubtitle: "We follow a proven, collaborative process to ensure every project is delivered on time, meets your goals, and creates real impact.",
    processSteps: defaultProcessSteps
  });

  const activeProcessSteps = (Array.isArray(portfolioCMS.processSteps) && portfolioCMS.processSteps.length > 0)
    ? portfolioCMS.processSteps
    : defaultProcessSteps;

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchProjects = async () => {
      try {
        setLoading(true);
        const baseUrl = window.API_URL || "http://localhost:8000";
        const res = await axios.get(`${baseUrl}/projects`);
        if (Array.isArray(res.data)) {
          // Strictly show ONLY projects added through Admin Panel
          const apiProjects = [...res.data].reverse().map((item) => ({
            id: item._id || item.id,
            title: item.title,
            desc: item.desc,
            category: getCategory(item),
            image: getProjectImageUrl(item),
            link: item.link || "/portfolio"
          }));
          setProjects(apiProjects);
        } else {
          setProjects([]);
        }
      } catch (err) {
        console.error("Error fetching projects from admin panel:", err);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Filter projects by category tab
  const filteredProjects = projects.filter((item) => {
    if (activeTab === "All") return true;
    const cat = (item.category || "").toLowerCase();
    if (activeTab === "Websites") {
      return cat.includes("website") || cat.includes("commerce") || cat.includes("web development");
    }
    if (activeTab === "AI Solutions") {
      return cat.includes("ai") || cat.includes("bot") || cat.includes("intelligence");
    }
    if (activeTab === "Digital Marketing") {
      return cat.includes("marketing") || cat.includes("seo") || cat.includes("growth") || cat.includes("tourism");
    }
    if (activeTab === "Web-Apps") {
      return cat.includes("app") || cat.includes("saas") || cat.includes("application") || cat.includes("portal") || cat.includes("management");
    }
    return true;
  });

  const nextTestimonial = () => {
    setActiveTestimonialIdx((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonialIdx((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="bg-white min-h-screen text-slate-900 font-sans selection:bg-[#f06600] selection:text-white w-full overflow-x-hidden">
      
      {/* ──────────────────────────────────────────────────────────
          01. HERO SECTION (Dark futuristic agency with workstation)
         ────────────────────────────────────────────────────────── */}
      <section className="bg-[#080c14] relative overflow-hidden text-white pt-10 pb-16 lg:pt-16 lg:pb-24 border-b border-slate-800/80">
        
        {/* Ambient Glows */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#f06600]/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Column: Heading & CTAs */}
            <div className="lg:col-span-6 space-y-6">
              
              {/* Category Pill */}
              <div className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] animate-pulse"></span>
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#f06600]">
                  {portfolioCMS.heroEyebrow || "OUR WORK"}
                </span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.12]">
                {portfolioCMS.heroTitleLine1 || "Ideas Turned Into"} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f06600] via-orange-400 to-[#f06600]">
                  {portfolioCMS.heroHighlight || "Real Results"}
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-sm sm:text-base text-slate-300 max-w-xl font-normal leading-relaxed">
                {portfolioCMS.heroSubtitle || "Explore how we've helped businesses build, scale and transform through design, development, AI and digital marketing"}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Link
                  to="/contactus"
                  className="inline-flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 sm:px-7 py-3.5 rounded-full text-xs sm:text-sm tracking-wide shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <span>Start a Similar Project</span>
                  <BsArrowRight className="text-sm" />
                </Link>

                <button
                  type="button"
                  onClick={() => setShowVideoModal(true)}
                  className="inline-flex items-center gap-2.5 bg-[#101726]/90 hover:bg-[#162035] text-white border border-slate-700/80 hover:border-slate-500 font-bold px-6 py-3.5 rounded-full text-xs sm:text-sm tracking-wide transition-all duration-300 cursor-pointer group shadow-md"
                >
                  <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-xs group-hover:bg-[#f06600] group-hover:text-white transition-colors">
                    <BsPlayFill className="text-xs ml-0.5" />
                  </span>
                  <span>Watch Showreel</span>
                </button>
              </div>

              {/* Key Metrics Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-slate-800/80">
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-[#f06600] tracking-tight">
                    250+
                  </div>
                  <div className="text-xs text-slate-400 font-medium mt-0.5">
                    Projects Delivered
                  </div>
                </div>

                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-[#f06600] tracking-tight">
                    100+
                  </div>
                  <div className="text-xs text-slate-400 font-medium mt-0.5">
                    Happy Clients
                  </div>
                </div>

                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-[#f06600] tracking-tight">
                    15+
                  </div>
                  <div className="text-xs text-slate-400 font-medium mt-0.5">
                    Industries Served
                  </div>
                </div>

                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-[#f06600] tracking-tight">
                    99%
                  </div>
                  <div className="text-xs text-slate-400 font-medium mt-0.5">
                    Client Satisfaction
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Workstation Showcase Image with Floating Tags */}
            <div className="lg:col-span-6 relative">
              
              <div className="relative mx-auto max-w-lg lg:max-w-none">
                
                {/* Main Workstation Graphic */}
                <div className="relative rounded-2xl overflow-hidden border border-slate-700/60 shadow-2xl shadow-black/60 bg-slate-900 group">
                  <img
                    src="/assets/work_hero_workspace.jpg"
                    alt="Developer Workspace"
                    className="w-full h-auto aspect-[16/10] sm:aspect-[16/9] object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/hero_dev_workspace.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#080c14] via-transparent to-black/20 pointer-events-none"></div>
                </div>

                {/* Floating Handwritten Badge: Real Projects Real Impact */}
                <div className="absolute -top-4 right-2 sm:right-6 bg-[#0c1220]/95 backdrop-blur-md border border-orange-500/40 px-4 py-2 rounded-2xl shadow-xl transform rotate-3 flex items-center gap-2">
                  <span className="font-serif italic text-xs sm:text-sm font-bold text-orange-400 tracking-wide">
                    Real Projects, Real Impact ⚡
                  </span>
                </div>

                {/* Floating Bottom Right Dark Glass Card: Services Stack */}
                <div className="absolute -bottom-6 right-2 sm:right-6 bg-[#0a0f1d]/95 backdrop-blur-md border border-slate-700/80 p-3.5 sm:p-4 rounded-xl shadow-2xl space-y-2 text-xs font-medium text-slate-200">
                  <div className="flex items-center gap-2 text-slate-300">
                    <BsGlobe className="text-[#f06600] text-sm shrink-0" />
                    <span>Web Development</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <BsRobot className="text-cyan-400 text-sm shrink-0" />
                    <span>AI Solutions</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <BsMegaphone className="text-amber-400 text-sm shrink-0" />
                    <span>Digital Marketing</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <BsLayers className="text-purple-400 text-sm shrink-0" />
                    <span>Web Applications</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <BsPalette className="text-pink-400 text-sm shrink-0" />
                    <span>UI/UX Design</span>
                  </div>
                </div>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ──────────────────────────────────────────────────────────
          02. CLIENT LOGOS BAR (Trusted By Businesses Worldwide)
         ────────────────────────────────────────────────────────── */}
      <section className="bg-white border-b border-slate-100 py-10 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400 mb-6 sm:mb-8">
            TRUSTED BY BUSINESSES WORLDWIDE
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-8 items-center justify-items-center opacity-80 hover:opacity-100 transition-opacity">
            
            {/* Shopify */}
            <div className="flex items-center gap-2 text-slate-600 font-bold text-base hover:text-green-600 transition-colors">
              <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                <path d="M19.64 6.75l-1.39-4.39a.74.74 0 00-.7-.52H6.45a.75.75 0 00-.7.52L4.36 6.75A2.25 2.25 0 003 8.87V19.5A2.25 2.25 0 005.25 21.75h13.5A2.25 2.25 0 0021 19.5V8.87a2.25 2.25 0 00-1.36-2.12zm-8.89-3.41h2.5v2.25h-2.5V3.34zm6.5 16.16a.75.75 0 01-.75.75H4.5a.75.75 0 01-.75-.75V8.87a.75.75 0 01.46-.7l1.39-4.39a.75.75 0 01.7-.53h1.45v3.75h6.5V3.25h1.45a.75.75 0 01.7.53l1.39 4.39a.75.75 0 01.46.7v10.63z"/>
              </svg>
              <span>shopify</span>
            </div>

            {/* Google */}
            <div className="flex items-center gap-1.5 text-slate-600 font-bold text-base hover:text-blue-600 transition-colors">
              <span className="text-xl">G</span>
              <span>Google</span>
            </div>

            {/* Meta */}
            <div className="flex items-center gap-1.5 text-slate-600 font-bold text-base hover:text-blue-500 transition-colors">
              <span className="text-xl font-extrabold">∞</span>
              <span>Meta</span>
            </div>

            {/* Figma */}
            <div className="flex items-center gap-1.5 text-slate-600 font-bold text-base hover:text-purple-600 transition-colors">
              <span className="font-extrabold text-sm border-2 border-current px-1 rounded-sm">Fg</span>
              <span>Figma</span>
            </div>

            {/* WordPress */}
            <div className="flex items-center gap-1.5 text-slate-600 font-bold text-base hover:text-sky-700 transition-colors">
              <span className="w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-xs font-bold">W</span>
              <span>WordPress</span>
            </div>

            {/* Laravel */}
            <div className="flex items-center gap-1.5 text-slate-600 font-bold text-base hover:text-red-500 transition-colors">
              <span className="text-lg">▲</span>
              <span>Laravel</span>
            </div>

          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          03. FEATURED PROJECTS (OUR PORTFOLIO) - Admin Panel Projects ONLY
         ────────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-white text-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header Row */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            
            {/* Left: Tag, Heading & Subtitle */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600]"></span>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f06600]">
                  OUR PORTFOLIO
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                Featured <span className="text-[#f06600]">Projects</span>
              </h2>

              <p className="text-xs sm:text-sm text-slate-500 max-w-xl font-normal pt-1">
                A selection of our recent work that showcases our capabilities, creativity, and the results we deliver.
              </p>
            </div>

            {/* Right: Category Filter Tabs */}
            <div className="flex flex-wrap items-center gap-2">
              {filterCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${
                    activeTab === cat
                      ? "bg-[#f06600] text-white shadow-md shadow-orange-500/25"
                      : "bg-white hover:bg-slate-50 text-slate-600 border border-slate-200"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

          </div>

          {/* Loading Skeleton */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="bg-white rounded-2xl border border-slate-200 p-4 animate-pulse space-y-4">
                  <div className="h-44 bg-slate-200 rounded-xl"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                  <div className="h-5 bg-slate-200 rounded w-2/3"></div>
                  <div className="h-3 bg-slate-200 rounded w-full"></div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredProjects.length === 0 && (
            <div className="text-center py-16 px-4 bg-slate-50 border border-slate-200 rounded-2xl">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-orange-100 text-[#f06600] flex items-center justify-center text-2xl mb-4">
                <BsFolderFill />
              </div>
              <h3 className="text-lg font-bold text-slate-800">
                No Projects Found in this Category
              </h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto mt-1 mb-6">
                {activeTab === "All" 
                  ? "No projects have been added yet through the Admin Panel." 
                  : `There are currently no projects matching "${activeTab}".`}
              </p>
              {activeTab !== "All" && (
                <button
                  onClick={() => setActiveTab("All")}
                  className="bg-[#f06600] text-white text-xs font-bold px-5 py-2 rounded-full cursor-pointer hover:bg-orange-600 transition-colors"
                >
                  View All Projects
                </button>
              )}
            </div>
          )}

          {/* 4-Column Responsive Grid matching the exact design and sizing */}
          {!loading && filteredProjects.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredProjects.map((proj) => {
                const isExternal = proj.link && (proj.link.startsWith("http://") || proj.link.startsWith("https://"));

                const CardContent = (
                  <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-xl hover:border-orange-300 transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between overflow-hidden group h-full cursor-pointer">
                    
                    {/* Top Thumbnail Image with Fixed Proportions */}
                    <div className="relative h-44 sm:h-48 w-full overflow-hidden bg-slate-100 border-b border-slate-100">
                      <img
                        src={proj.image}
                        alt={proj.title}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/assets/project_ecommerce.jpg";
                        }}
                      />
                    </div>

                    {/* Card Content Body */}
                    <div className="p-4 sm:p-5 flex flex-col flex-1 justify-between gap-3">
                      
                      <div>
                        {/* Category Badge & Diagonal Arrow Icon Row */}
                        <div className="flex items-center justify-between gap-2 mb-2.5">
                          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
                            {proj.category}
                          </span>
                          <BsArrowUpRight className="text-slate-400 group-hover:text-[#f06600] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-sm font-bold" />
                        </div>

                        {/* Project Title */}
                        <h3 className="text-base font-bold text-slate-900 group-hover:text-[#f06600] transition-colors leading-snug line-clamp-1">
                          {proj.title}
                        </h3>

                        {/* Project Description (2 lines max) */}
                        <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mt-1.5">
                          {proj.desc}
                        </p>
                      </div>

                      {/* Bottom Action Link */}
                      <div className="pt-2 border-t border-slate-100 flex items-center">
                        <span className="text-xs font-semibold text-[#f06600] flex items-center gap-1 group-hover:gap-2 transition-all">
                          <span>View Project</span>
                          <BsArrowRight className="text-xs" />
                        </span>
                      </div>

                    </div>

                  </div>
                );

                return isExternal ? (
                  <a
                    key={proj.id}
                    href={proj.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block h-full"
                  >
                    {CardContent}
                  </a>
                ) : (
                  <Link
                    key={proj.id}
                    to={proj.link === "/portfolio" ? "/contactus" : proj.link}
                    className="block h-full"
                  >
                    {CardContent}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Mini CTA Banner directly beneath the project cards */}
          <div className="bg-[#fff8f2] border border-orange-200/80 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 mt-12 shadow-sm">
            <div className="flex items-center gap-4 text-center sm:text-left">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-[#f06600] text-xl shrink-0 mx-auto sm:mx-0">
                <BsFolderFill />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm sm:text-base">
                  Have a project in mind?
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Let's turn your ideas into something amazing.
                </p>
              </div>
            </div>

            <Link
              to="/contactus"
              className="bg-[#f06600] hover:bg-orange-600 text-white text-xs font-bold px-6 py-3 rounded-full transition-all duration-300 shadow-md shadow-orange-500/20 flex items-center gap-1.5 shrink-0"
            >
              <span>Start Your Project</span>
              <BsArrowRight className="text-xs" />
            </Link>
          </div>

        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          04. OUR PROCESS (How We Turn Ideas Into Successful Projects)
         ────────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 sm:py-20 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600]"></span>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f06600]">
                  OUR PROCESS
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                {portfolioCMS.processHeading || "How We Turn Ideas Into Successful Projects"}
              </h2>
            </div>

            <p className="text-xs sm:text-sm text-slate-500 max-w-md font-normal leading-relaxed">
              {portfolioCMS.processSubtitle || "We follow a proven, collaborative process to ensure every project is delivered on time, meets your goals, and creates real impact."}
            </p>
          </div>

          {/* Desktop 6-Column Grid (lg and above) */}
          <div className="hidden lg:grid lg:grid-cols-6 gap-4">
            {activeProcessSteps.map((step, idx) => {
              const icons = [
                <BsSearch className="text-xl" />,
                <BsCalendarCheck className="text-xl" />,
                <BsPalette className="text-xl" />,
                <BsCodeSlash className="text-xl" />,
                <BsRocketTakeoff className="text-xl" />,
                <BsGraphUpArrow className="text-xl" />
              ];
              return (
                <div
                  key={step.num || idx}
                  className="bg-white border border-slate-200/90 hover:border-orange-300 rounded-xl p-4 sm:p-5 flex flex-col justify-between transition-all duration-300 hover:shadow-lg group"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="w-7 h-7 rounded-full bg-orange-50 text-[#f06600] font-bold text-xs flex items-center justify-center border border-orange-200">
                        {step.num}
                      </span>
                      <span className="text-[#f06600] group-hover:scale-110 transition-transform">
                        {icons[idx % icons.length]}
                      </span>
                    </div>

                    <h3 className="font-bold text-slate-900 text-sm mb-1 group-hover:text-[#f06600] transition-colors">
                      {step.title}
                    </h3>

                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile & Tablet Milestone Vertical Timeline (< lg: single card in one row with side line) */}
          <div className="block lg:hidden relative pt-2 pb-2">
            {/* Continuous Vertical Connecting Line */}
            <div className="absolute left-[17px] sm:left-[21px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-[#f06600] via-orange-400 to-orange-200"></div>

            <div className="space-y-4 sm:space-y-6 relative z-10">
              {activeProcessSteps.map((step, sIdx) => {
                const icons = [
                  <BsSearch className="text-base" />,
                  <BsCalendarCheck className="text-base" />,
                  <BsPalette className="text-base" />,
                  <BsCodeSlash className="text-base" />,
                  <BsRocketTakeoff className="text-base" />,
                  <BsGraphUpArrow className="text-base" />
                ];
                return (
                  <div
                    key={step.num || sIdx}
                    className="relative flex items-start pl-11 sm:pl-14 group"
                  >
                    {/* Milestone dot on the vertical side line */}
                    <div className="absolute left-[8px] sm:left-[12px] top-3.5 w-5 h-5 rounded-full bg-white border-2 border-[#f06600] shadow-[0_0_0_2px_#fed7aa] z-10 flex items-center justify-center transition-transform duration-300 group-hover:scale-125">
                      <div className="w-2 h-2 rounded-full bg-[#f06600]"></div>
                    </div>

                    {/* Single Card per Row */}
                    <div className="flex-1 bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md hover:border-orange-300 transition-all duration-300">
                      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                        <div className="flex items-center gap-2.5">
                          <span className="text-xs font-black uppercase tracking-wider text-[#f06600] bg-orange-50 px-2.5 py-1 rounded-lg border border-orange-200/70">
                            Step {step.num}
                          </span>
                          <h3 className="font-extrabold text-slate-900 text-sm sm:text-base group-hover:text-[#f06600] transition-colors">
                            {step.title}
                          </h3>
                        </div>
                        <div className="w-8 h-8 rounded-xl bg-orange-500/10 text-[#f06600] flex items-center justify-center text-sm">
                          {icons[sIdx % icons.length]}
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          05. CLIENT SUCCESS / TESTIMONIALS (Dark Section)
         ────────────────────────────────────────────────────────── */}
      <section className="bg-[#070b14] text-white py-16 sm:py-20 border-t border-slate-800/80 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600]"></span>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f06600]">
                  CLIENT SUCCESS
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                What Our <span className="text-[#f06600]">Clients Say</span>
              </h2>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <p className="text-xs text-slate-400 max-w-sm">
                Long-term partnerships and real results are what drive us. Here's what some of our clients have to say.
              </p>
              <Link
                to="/aboutus"
                className="inline-flex items-center gap-1.5 border border-slate-700 hover:border-slate-500 px-4 py-2 rounded-full text-xs font-semibold text-slate-300 hover:text-white transition-colors shrink-0"
              >
                <span>View All Reviews</span>
                <BsArrowRight className="text-xs" />
              </Link>
            </div>
          </div>

          {/* Testimonial Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                className="bg-[#0e1422] border border-slate-800 rounded-2xl p-6 sm:p-7 flex flex-col justify-between hover:border-slate-700 transition-all duration-300"
              >
                <div>
                  <div className="text-3xl text-orange-500/80 font-serif leading-none mb-3">
                    “
                  </div>
                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-normal">
                    {t.quote}
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-800/80">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover border border-slate-700"
                  />
                  <div>
                    <h4 className="text-xs sm:text-sm font-bold text-white leading-tight">
                      {t.name}
                    </h4>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {t.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Slider Controls */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="w-8 h-8 rounded-full border border-slate-700 hover:border-slate-500 flex items-center justify-center text-slate-400 hover:text-white transition-colors cursor-pointer"
              aria-label="Previous testimonial"
            >
              <BsChevronLeft className="text-xs" />
            </button>

            <div className="flex items-center gap-1.5">
              <span className="w-5 h-1.5 rounded-full bg-[#f06600]"></span>
              <span className="w-1.5 h-1.5 rounded-full bg-slate-700"></span>
              <span className="w-1.5 h-1.5 rounded-full bg-slate-700"></span>
            </div>

            <button
              onClick={nextTestimonial}
              className="w-8 h-8 rounded-full border border-slate-700 hover:border-slate-500 flex items-center justify-center text-slate-400 hover:text-white transition-colors cursor-pointer"
              aria-label="Next testimonial"
            >
              <BsChevronRight className="text-xs" />
            </button>
          </div>

        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          06. CTA BANNER (Ready to Create Your Success Story?)
         ────────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="bg-gradient-to-r from-[#f06600] to-[#ff7a1a] rounded-3xl p-8 sm:p-12 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl shadow-orange-500/25">
          
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-white"></span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/90">
                LET'S BUILD TOGETHER
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight">
              Ready to Create Your Success Story?
            </h2>

            <p className="text-xs sm:text-sm text-white/90 max-w-xl font-medium">
              Join hundreds of businesses that have turned their ideas into reality with us.
            </p>
          </div>

          <Link
            to="/contactus"
            className="bg-white hover:bg-orange-50 text-[#f06600] font-bold px-8 py-3.5 rounded-full text-xs sm:text-sm tracking-wide shadow-lg transition-all duration-300 transform hover:scale-105 shrink-0 flex items-center gap-1.5"
          >
            <span>Let's Talk</span>
            <BsArrowRight className="text-sm" />
          </Link>

        </div>
      </section>

      {/* Video Modal (Showreel) */}
      {showVideoModal && (
        <div className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative bg-[#0d1320] border border-slate-700 rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h3 className="text-white font-bold text-sm">
                Beta Communes · Agency Showreel
              </h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="text-slate-400 hover:text-white text-xl p-1 cursor-pointer"
                aria-label="Close modal"
              >
                <BsX />
              </button>
            </div>
            <div className="aspect-video bg-black flex items-center justify-center">
              <iframe
                className="w-full h-full"
                src="https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ?autoplay=1"
                title="Agency Showreel"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Portfolio;